import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';
import api from '../utils/api';

const STATUSES = ['new', 'contacted', 'converted', 'rejected'];

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  converted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const CATEGORY_LABELS = { mens: "Men's", womens: "Women's", unisex: 'Unisex' };

const PartnerLeads = () => {
  const [leads, setLeads] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/partner-lead', {
        params: { page, limit: 20, status: statusFilter || undefined, search: search || undefined },
      });
      if (response.data.success) {
        setLeads(response.data.data.leads);
        setStatusCounts(response.data.data.statusCounts);
        setPagination(response.data.data.pagination);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching partner leads:', err);
      setError(err.response?.data?.message || 'Failed to load partner leads');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleStatusFilter = (status) => {
    setPage(1);
    setStatusFilter(status);
  };

  const updateLead = async (leadId, changes) => {
    try {
      setUpdatingId(leadId);
      const response = await api.patch(`/partner-lead/${leadId}`, changes);
      if (response.data.success) {
        setLeads((prev) => prev.map((lead) => (lead._id === leadId ? response.data.data : lead)));
        if (changes.status) fetchLeads();
      }
    } catch (err) {
      console.error('Error updating partner lead:', err);
      alert(err.response?.data?.message || 'Failed to update lead');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleNotesBlur = (lead, value) => {
    if ((lead.adminNotes || '') !== value.trim()) {
      updateLead(lead._id, { adminNotes: value });
    }
  };

  const totalLeads = STATUSES.reduce((sum, status) => sum + (statusCounts[status] || 0), 0);

  return (
    <div className="space-y-5">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Partner Leads</h1>
        <p className="text-gray-600">Salon owner applications submitted from the website</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          type="button"
          onClick={() => handleStatusFilter('')}
          className={`px-4 py-2 rounded-lg text-sm border ${statusFilter === '' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-200'}`}
        >
          All ({totalLeads})
        </button>
        {STATUSES.map((status) => (
          <button
            type="button"
            key={status}
            onClick={() => handleStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm border capitalize ${statusFilter === status ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-200'}`}
          >
            {status} ({statusCounts[status] || 0})
          </button>
        ))}
      </div>

      <div className="flex justify-between mb-6 gap-4">
        <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search salon, owner, phone or city"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
            Search
          </button>
        </form>
        <button
          type="button"
          onClick={fetchLeads}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700"
        >
          <FiRefreshCw size={14} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">{error}</div>
      )}

      <div className="bg-white rounded-3xl shadow-card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Received', 'Salon', 'Owner', 'Contact', 'City', 'Type', 'Message', 'Status', 'Notes'].map((heading) => (
                <th key={heading} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">Loading...</td>
              </tr>
            )}
            {!loading && leads.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">No partner leads found</td>
              </tr>
            )}
            {!loading && leads.map((lead) => (
              <tr key={lead._id} className="align-top">
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                  {moment(lead.createdAt).format('DD MMM YYYY, h:mm A')}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{lead.salonName}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{lead.ownerName}</td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  <a href={`tel:${lead.phoneNumber}`} className="text-blue-600">{lead.phoneNumber}</a>
                  {lead.email && (
                    <div>
                      <a href={`mailto:${lead.email}`} className="text-blue-600">{lead.email}</a>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{lead.city}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{CATEGORY_LABELS[lead.category] || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs whitespace-pre-wrap break-words">
                  {lead.message || '-'}
                </td>
                <td className="px-4 py-3 text-sm">
                  <select
                    value={lead.status}
                    disabled={updatingId === lead._id}
                    onChange={(e) => updateLead(lead._id, { status: e.target.value })}
                    className={`px-2 py-1 rounded-full text-xs font-medium capitalize border-0 ${STATUS_STYLES[lead.status] || ''}`}
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-sm">
                  <textarea
                    defaultValue={lead.adminNotes || ''}
                    onBlur={(e) => handleNotesBlur(lead, e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="Add a note"
                    className="w-48 px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-4">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1 bg-white border border-gray-200 rounded text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.pages}</span>
          <button
            type="button"
            disabled={page >= pagination.pages}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1 bg-white border border-gray-200 rounded text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PartnerLeads;
